import {
    NavigateFunction,
    useLocation,
    useNavigate,
} from 'react-router-dom'

/**
 * Update a single search-param query in the current URL.
 *
 * When `value` is falsy the key is removed instead of being set.
 *
 * @param navigate React Router 7 navigate function
 * @param pathname Current pathname (basename-stripped, as returned by useLocation)
 * @param search Current search string (with leading `?`)
 * @param key Query key to update
 * @param value New value, or null/empty string to remove the key
 */
export const addUrlQuery = (
    navigate: NavigateFunction,
    pathname: string,
    search: string,
    key: string,
    value: string | number | null,
): void => {
    const searchParams = new URLSearchParams(search)

    if (value === null || value === undefined || value === '') {
        searchParams.delete(key)
    } else {
        searchParams.set(key, String(value))
    }

    navigate({
        pathname,
        search: searchParams.toString(),
    })
}

/**
 * React hook returning a setter `(key, value) => void` that updates a
 * URL query parameter in place.
 *
 * Reads `window.location.search` (not the captured router `location.search`)
 * so that several `addUrlQuery` calls invoked sequentially within the same
 * event handler can be chained — each one observes the previous one's
 * `navigate(...)` (history.pushState) instead of overwriting it with the
 * stale per-render search string.
 */
export const useAddUrlQuery = () => {
    const navigate = useNavigate()
    const location = useLocation()

    return (key: string, value: string | number | null): void =>
        addUrlQuery(navigate, location.pathname, window.location.search, key, value)
}
